// What the recorder says, for the device in use. The same panel runs on a
// desktop (/record), on the operator's phone and on a child's phone
// (/companion/record), so nothing here names a channel ("Telegram") or
// points a child at operator-only screens.

import { formatBytes } from "@/lib/format";
import type { RecorderPhase } from "./recorderEngine";
import type { CarryOnOffer } from "./recorderCore";
import { NOTHING_RECORDED, QUIET_WARN_SEC, cutShortLine } from "./recorderModel";
import { formatTime } from "@/lib/format";
import type { Waiting } from "./recorderTypes";
import { WARNING_NOTICES, callSoundWarning, type CallSoundState, type CallVia } from "./sourceModel";

export interface CopyContext {
  /** A phone or a home-screen install (touch-first, the phone sits on the table). */
  phone: boolean;
  /** A family (child) login: no More tab, no operator settings. */
  child: boolean;
  /** An iPhone or iPad: its browser stops the microphone when the screen locks. */
  apple?: boolean;
  /** Where unsent audio waits: "memory" when this browser keeps no copy on the device (nothing survives a closed page). */
  storage?: "device" | "memory";
}

export function recorderCopy({ phone, child, apple = false, storage = "device" }: CopyContext) {
  return {
    unsupported: "This browser can't record audio. On iPhone, open this in Safari or from the home-screen app.",
    micDenied: phone
      ? child
        ? "The microphone is blocked for this app, so recording can't start. Ask a grown-up to allow it in the phone's settings."
        : "The microphone is blocked for this app, so recording can't start. Open More, then Microphone, for how to turn it back on."
      : "The microphone is blocked for this site, so recording can't start. Allow it in your browser's site settings, then reload the page.",
    micPrompt: phone
      ? "The first time you record, your phone will ask for the microphone. Tap Allow."
      : "Your browser will ask for the microphone the first time. Choose Allow.",
    /** A call recording: after the share, the browser asks for the microphone here, for your side. */
    micPromptCall: "After you share the call, allow the microphone here so your side is recorded too.",
    /** A call recording in the Mac app: macOS asks for this Mac's sound (and the microphone), no share. */
    micPromptMac: "The first time, your Mac asks to let Corvus record its sound and the microphone. Choose Allow.",
    noAgent: child
      ? "Recording isn't switched on for you yet."
      : phone
        ? "Switch meetings on for an agent first: on a computer, open Meetings, then Follow-up settings."
        : "Switch meetings on for an agent first: Meetings, then Follow-up settings.",
    guidance: apple
      ? "On an iPhone, the Corvus app records best: the browser stops the microphone when the screen locks or you switch apps. Here, keep the screen on with this page open."
      : phone
        ? "Put the phone on the table with the screen on and say you're recording. It stays awake by itself; locking it or switching apps stops the microphone."
        : storage === "device"
          ? "Closing this tab stops recording; what was recorded is kept and sent."
          : "This browser keeps no copy: closing this tab loses what has not uploaded yet.",
  };
}

/**
 * Why the upload waits, in words; "" when it does not. `name` names the
 * recording when it is not the one the screen shows ("The recording of X").
 * Every waiting reason reads as whole sentences for both kinds of storage:
 * on the device, the recording is kept there; in memory, the page must stay open.
 */
export function waitingLine(waiting: Waiting, storage: "device" | "memory", refusal: string | null, name: string | null = null): string {
  const device = storage === "device";
  const what = name ? `recording of ${name}` : "recording";
  const it = `The ${what}`;
  // The same words as the footer's: what is lost is what has not uploaded yet, never "the recording".
  const noCopy = `this browser keeps no copy of the ${what} on this device, so closing the page loses what has not uploaded yet`;
  switch (waiting) {
    case "offline":
      return device
        ? `Can't reach Corvus. ${it} is kept on this device and is sent when the connection is back.`
        : `Can't reach Corvus. Keep this page open until the connection is back: ${noCopy}.`;
    case "signed_out":
      return device
        ? `Your session has ended. Sign in again: the ${what} is kept on this device and sent after you sign in.`
        : `Your session has ended. Sign in again and keep this page open: ${noCopy}.`;
    case "storage_full":
      return device
        ? `Corvus's storage is full. ${it} is kept on this device and is sent when there is room.`
        : `Corvus's storage is full. Keep this page open until there is room: ${noCopy}.`;
    case "refused":
      if (refusal === "meetings_disabled") return "Meetings are switched off for this workspace, so Corvus won't take the recording. Save the audio to a file below.";
      return device
        ? `${refusalWords(refusal)} ${it} is kept on this device; Corvus tries again every few minutes, or save the audio to a file.`
        : `${refusalWords(refusal)} Corvus tries again every few minutes; keep this page open (${noCopy}), or save the audio to a file.`;
    default:
      return "";
  }
}

/**
 * While nothing waits but this browser keeps no copy on the device: keep the
 * page open until it has uploaded. "" with device storage, while a waiting
 * line already says it, or when nothing is left to send.
 */
export function noCopyLine(s: { storage: "device" | "memory"; waiting: Waiting; phase: RecorderPhase; unsent: number }): string {
  if (s.storage !== "memory" || s.waiting !== "none") return "";
  const inFlight = s.phase === "requesting" || s.phase === "recording" || s.phase === "stopping" || s.phase === "uploading" || s.unsent > 0;
  return inFlight ? "This browser keeps no copy of the recording on this device, so keep this page open until it has uploaded." : "";
}

/** What the recording bar says under its first line: why it waits, else the microphone (or a call's lost side), else that this browser keeps no copy. */
export function barWords(s: {
  waiting: Waiting; storage: "device" | "memory"; refusal: string | null; phase: RecorderPhase; unsent: number; muted: boolean; quietSec: number;
  /** A call recording live: one side gone is said (the share ended, the microphone went). */
  callSound?: CallSoundState | null;
  /** A call recording whose call side is silent while the microphone hears speech. */
  callSilent?: boolean;
  /** Where the call side comes from (the Mac app's own capture says "this Mac"), and whether the Mac stopped allowing it. */
  callVia?: CallVia | null;
  callLost?: "denied" | null;
}): string {
  const recording = s.phase === "recording" || s.phase === "stopping";
  return waitingLine(s.waiting, s.storage, s.refusal) || (recording ? liveWarning(s) : "") || noCopyLine(s);
}

/** Every warning while recording, in one line: the microphone (or, on a call, the side that is quiet), else a call side gone. */
export function liveWarning(s: { muted: boolean; quietSec: number; callSound?: CallSoundState | null; callSilent?: boolean; callVia?: CallVia | null; callLost?: "denied" | null }): string {
  return micWarning(s) || callSoundWarning(s.callSound ?? null, !!s.callSilent, s.callVia ?? "share", s.callLost ?? null);
}

function refusalWords(refusal: string | null): string {
  if (refusal === "wrong_upload") return "Another device took over this meeting's recording.";
  if (refusal === "HTTP 413") return "Corvus refused a part of the recording as too large.";
  if (refusal === "HTTP 403") return "This sign-in can't add recordings to this workspace.";
  return "Corvus didn't take the recording.";
}

/**
 * The sign-out dialog's question (D9). With storage on the device, what was
 * captured waits there for the next sign-in; when this browser keeps no copy,
 * it waits only in this page, so the dialog says to keep the page open.
 */
export function signOutBody(storage: "device" | "memory"): string {
  return storage === "device"
    ? "Stop and upload it first, or sign out anyway? (The recording stops; what was captured stays on this device and is sent the next time you sign in.)"
    : "Stop and upload it first, or sign out anyway? (The recording stops. This browser keeps no copy on this device: what has not uploaded yet is sent only if you sign in again on this page before closing it.)";
}

/** The "Carry on?" question's sentence, for the bar and the Record screen. */
export function offerLine(o: CarryOnOffer, storage: "device" | "memory" = "device"): string {
  // Saved, but not all of it on the server yet: said, so nobody closes the page thinking it is.
  // With no device storage the parts are only in this page (never "on this device").
  const where = storage === "memory" ? "in this page" : "on this device";
  const onDevice = o.unsent ? ` ${o.unsent === 1 ? "One part is" : `${o.unsent} parts are`} still ${where}, waiting to upload.` : "";
  if (o.finishAt !== null) {
    return `${cutShortLine(o.title, o.at, o.callSound && o.reason === "microphone_lost" ? "call_lost" : o.reason, o.savedSec)}${onDevice} Carry on to add to it, or it goes for notes at ${formatTime(new Date(o.finishAt))}.`;
  }
  if (o.reason === "microphone_lost" && o.callSound) {
    // a call take ends this way only once its share AND its microphone have both gone
    return `The call's sound and the microphone stopped at ${formatTime(new Date(o.at))}. What was recorded of ${o.title} is saved and goes for notes; carry on to add to it.${onDevice}`;
  }
  if (o.reason === "microphone_lost") {
    return `The microphone stopped at ${formatTime(new Date(o.at))}. What was recorded of ${o.title} is saved and goes for notes; carry on to add to it.${onDevice}`;
  }
  return `${cutShortLine(o.title, o.at, o.reason, o.savedSec)} It has gone for notes; carry on to add to it.${onDevice}`;
}

/** Under the timer while an earlier recording still uploads beside this one, or "". */
export function pendingLine(n: number): string {
  if (n <= 0) return "";
  return n === 1 ? "An earlier recording is still uploading; it finishes by itself." : `${n} earlier recordings are still uploading; they finish by themselves.`;
}

/** A recording kept on this device to save as a file: parts Corvus already held other audio for, or one it found no audio in. */
export function keptLine(title: string, parts: number, noAudio = false): string {
  if (noAudio) return `Corvus found no audio in the recording of ${title}, so it could not write it up. What is still on this device is kept: save it to a file, or remove it.`;
  return `${parts === 1 ? "A part" : `${parts} parts`} of the recording of ${title} could not be added to the meeting: Corvus already held other audio there. ${parts === 1 ? "It is" : "They are"} kept on this device; save ${parts === 1 ? "it" : "them"} to a file.`;
}

/**
 * A word about the microphone while recording, or "" (muted by the system, or a minute of
 * silence). On a call (`callSound`) the silence names what is actually listening: the call alone
 * when the microphone is off, both when both are on.
 */
export function micWarning(s: { muted: boolean; quietSec: number; callSound?: CallSoundState | null; callVia?: CallVia | null }): string {
  if (s.muted) return "The system has muted the microphone: nothing is being recorded until it comes back.";
  if (s.quietSec < QUIET_WARN_SEC) return "";
  const min = Math.round(s.quietSec / 60) || 1;
  const callOnly = s.callSound === "call_only" || s.callSound === "asking_mic";
  if (callOnly && s.callVia === "native") return `No sound from this Mac for ${min} min. Check the call is playing.`;
  if (callOnly) return `No sound from the call for ${min} min. Check it's the right tab and its sound is on.`;
  if (s.callSound === "both") return `No sound for ${min} min. Check the call is playing and the microphone isn't muted.`;
  return `No sound for ${min} min. Check the microphone is the right one and not muted.`;
}

/** The line under the timer while something is happening (nothing when idle). */
export function phaseStatus(
  phase: RecorderPhase,
  s: {
    reconnecting?: boolean; waiting?: Waiting; storage?: "device" | "memory"; refusal?: string | null;
    registered?: boolean; unsent?: number; bytesSent: number; etaMin: number | null; error: string | null;
    agentName: string; phone: boolean;
    /** The uploading recording's title, when it is not the one the screen's picker shows. */
    uploadingName?: string | null;
  },
): string {
  const waiting = s.waiting ?? (s.reconnecting ? "offline" : "none");
  const storage = s.storage ?? "device";
  const name = phase === "uploading" ? (s.uploadingName ?? null) : null;
  const wait = waitingLine(waiting, storage, s.refusal ?? null, name);
  const unsent = s.unsent ?? 0;
  switch (phase) {
    case "requesting":
      return "Asking for the microphone…";
    case "recording":
      if (wait) return `Recording. ${wait}`;
      if (s.registered && s.bytesSent > 0) return `Recording · ${formatBytes(s.bytesSent)} uploaded`;
      // Nothing has reached Corvus yet (no part sent, or every send still on its way): never "uploaded".
      return storage === "device" ? "Recording · saved on this device as you go" : "Recording · nothing uploaded yet";
    case "stopping":
      return "Stopping the microphone and saving the last seconds…";
    case "uploading":
      if (wait) return wait;
      // The count stays with its word ("5 parts to go" never splits over two lines).
      if (name) return unsent > 0 ? `Uploading the rest of ${name}… ${unsent}\u00a0${unsent === 1 ? "part" : "parts"} to go` : `Finishing the upload of ${name}…`;
      return unsent > 0 ? `Uploading the rest… ${unsent}\u00a0${unsent === 1 ? "part" : "parts"} to go` : "Finishing the upload…";
    case "transcribing":
      return `Transcribing${s.etaMin ? ` (about ${s.etaMin} min)` : ""}… ${s.phone ? "you can put the phone away." : "you can leave this page."}`;
    case "ready":
      return `Done. ${s.agentName} is writing the notes and will message you.`;
    case "failed":
      return s.error ?? "Something went wrong.";
    default:
      return "";
  }
}

/**
 * The word under the big button: what pressing it does, or what is
 * happening. After a failure the button starts a NEW recording; when the
 * audio was saved (transcription failed) "Try again" would read as a retry
 * of the transcription, which is the separate Retry transcription button.
 */
export function buttonLabel(phase: RecorderPhase, opts: { audioSaved?: boolean; continuing?: boolean } = {}): string {
  switch (phase) {
    case "requesting": return "Starting…";
    case "recording": return "Stop recording";
    case "stopping": return "Finishing…";
    // The earlier recording goes on uploading (or transcribing) by itself: the button records the next one.
    case "uploading": return opts.continuing ? "Carry on recording" : "Record another";
    case "transcribing": return opts.continuing ? "Carry on recording" : "Record another";
    case "ready": return "Record another";
    case "failed": return opts.audioSaved ? "New recording" : "Try again";
    case "idle": return opts.continuing ? "Carry on recording" : "Start recording";
    default: return "Start recording";
  }
}

/**
 * The picker shows the meeting whose recording is still uploading (or
 * transcribing) here: recording again adds to it, so the button says "Carry
 * on recording", never "Record another" (one meeting, one recording).
 */
export function addsToUploading(s: { phase: RecorderPhase; meeting: { id: string } | null; meetingId?: string | null }, pickedId: string | null): boolean {
  // A take this page took over from a closed one knows only its meeting's id.
  const id = s.meeting?.id ?? s.meetingId ?? null;
  return (s.phase === "uploading" || s.phase === "transcribing") && !!pickedId && id === pickedId;
}

/** Under the picker when it shows that meeting (addsToUploading). */
export const ADDS_TO_UPLOADING_HELP =
  "Recording now adds to this meeting's recording. To record something else, pick another meeting or start a new one.";

/**
 * The recording that is uploading, named when it is not the one the
 * picker shows (the person may already have picked the next meeting); null
 * otherwise, and whenever nothing is uploading.
 */
export function otherUploadName(
  s: { phase: RecorderPhase; title: string | null; meetingId: string | null },
  shown: { meetingId: string | null; title: string },
): string | null {
  if (s.phase !== "uploading" || !s.title) return null;
  // Filed under a meeting: named whenever the picker shows another one (even one given the same title).
  if (s.meetingId) return s.meetingId === shown.meetingId ? null : s.title;
  return s.title.trim() === shown.title.trim() ? null : s.title;
}

/** The colour of the line under the timer: a failure, something to act on (a wait, nothing recorded), or plain. */
export function statusTone(s: { phase: RecorderPhase; waiting: Waiting; notice: string | null }): "danger" | "warning" | "muted" {
  if (s.phase === "failed") return "danger";
  if (s.waiting !== "none") return "warning";
  return s.phase === "idle" && (s.notice === NOTHING_RECORDED || WARNING_NOTICES.includes(s.notice ?? "")) ? "warning" : "muted";
}

/** The big timer: the length already saved while a cut-short take waits for Carry on (never 00:00), else the live clock. */
export function timerSeconds(s: { capturing: boolean; elapsedSec: number; offer: { savedSec: number } | null }): number {
  return !s.capturing && s.offer ? Math.max(0, Math.round(s.offer.savedSec)) : s.elapsedSec;
}
